import { PortalReadState } from '../portal-read-state.tsx';
import { WorkPage } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFeedbackApi, AdminApiError } from '../../api/admin.ts';
import { Alert, Button, Checkbox, LoadingBlock, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';

/**
 * The feedback board's policy switches (spec §38).
 *
 * Every value here is stored in `directory_settings` and takes effect on the
 * next request — no deploy. That is the whole point of the screen: "raise the
 * allowance for this week" and "turn previews off while the environment is
 * down" are decisions somebody makes on a Tuesday afternoon.
 */
export default function AdminFeedbackSettingsPage() {
  useDocumentTitle('Feedback settings');
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin', 'feedback', 'settings'],
    queryFn: ({ signal }) => adminFeedbackApi.settings(signal),
  });

  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!query.data) return;
    setForm({
      enabled: query.data.enabled,
      weeklySubmissionLimit: String(query.data.weeklySubmissionLimit),
      voteRequirementEnabled: query.data.voteRequirementEnabled,
      requiredVotesPerMonth: String(query.data.requiredVotesPerMonth),
      newCompanyGraceDays: String(query.data.newCompanyGraceDays),
      moderationRequired: query.data.moderationRequired,
      previewEnabled: query.data.previewEnabled,
      previewSessionMinutes: String(query.data.previewSessionMinutes),
      aiDuplicateDetectionEnabled: query.data.aiDuplicateDetectionEnabled,
    });
  }, [query.data]);

  if (query.isLoading) return <PortalReadState title="Feedback settings" label="Loading settings…" />;
  if (query.isError || !query.data) {
    return (
      <PortalReadState title="Feedback settings" error="Settings could not be loaded." retry={() => query.refetch()} />
    );
  }

  const settings = query.data;

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await adminFeedbackApi.saveSettings({
        enabled: Boolean(form.enabled),
        weeklySubmissionLimit: Number.parseInt(String(form.weeklySubmissionLimit), 10) || 0,
        voteRequirementEnabled: Boolean(form.voteRequirementEnabled),
        requiredVotesPerMonth: Number.parseInt(String(form.requiredVotesPerMonth), 10) || 0,
        newCompanyGraceDays: Number.parseInt(String(form.newCompanyGraceDays), 10) || 0,
        moderationRequired: Boolean(form.moderationRequired),
        previewEnabled: Boolean(form.previewEnabled),
        previewSessionMinutes: Number.parseInt(String(form.previewSessionMinutes), 10) || 1440,
        aiDuplicateDetectionEnabled: Boolean(form.aiDuplicateDetectionEnabled),
      });
      setNotice('Saved. The new values apply to the next request.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    } catch (saveError) {
      setError(saveError instanceof AdminApiError ? saveError.message : String(saveError));
    } finally {
      setBusy(false);
    }
  }

  const set = (key: string, value: string | boolean) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <div className="portal-page">
      <WorkPage
        title="Feedback settings"
        description="Policy for the customer feedback board. Changes take effect immediately."
        actions={
          <Link className="btn" to="/admin/feedback">
            Back to Product Feedback
          </Link>
        }
      >
        {error ? (
          <div className="mb-4">
            <Alert tone="error" title="That could not be saved">
              {error}
            </Alert>
          </div>
        ) : null}
        {notice ? (
          <div className="mb-4">
            <Alert tone="success" title="Saved">
              {notice}
            </Alert>
          </div>
        ) : null}

        {!settings.previewConfigured ? (
          <div className="mb-6">
            <Alert tone="warning" title="Previews are not configured on this deployment">
              <p>
                Both halves of the bridge have to be set before "Try Preview" appears to anybody: the{' '}
                <code>PREVIEW_APP_URL</code> variable, and the <code>PREVIEW_LOGIN_SECRET</code> secret holding the SAME
                value on this deployment and on the preview one.
              </p>
            </Alert>
          </div>
        ) : (
          <p className="mb-6 text-sm text-[color:var(--color-muted)]">
            Previews open at <code>{settings.previewUrl}</code>.
          </p>
        )}

        <form className="card grid gap-5" onSubmit={save}>
          <Checkbox
            label="Feedback board enabled"
            hint="Off hides the navigation entry and refuses every feedback endpoint."
            checked={Boolean(form.enabled)}
            onChange={(event) => set('enabled', event.target.checked)}
          />

          <TextInput
            label="Weekly submission limit per company"
            hint="0 disables new submissions entirely. Existing ideas stay votable."
            inputMode="numeric"
            value={String(form.weeklySubmissionLimit ?? '')}
            onChange={(event) => set('weeklySubmissionLimit', event.target.value)}
          />

          <Checkbox
            label="Require monthly voting to keep submitting"
            hint="A company must vote on another company's idea each calendar month before it may submit again."
            checked={Boolean(form.voteRequirementEnabled)}
            onChange={(event) => set('voteRequirementEnabled', event.target.checked)}
          />

          <TextInput
            label="Qualifying votes required per month"
            inputMode="numeric"
            value={String(form.requiredVotesPerMonth ?? '')}
            onChange={(event) => set('requiredVotesPerMonth', event.target.value)}
          />

          <TextInput
            label="New company grace period (days)"
            hint="New customers may submit without having voted first. 30 is the default."
            inputMode="numeric"
            value={String(form.newCompanyGraceDays ?? '')}
            onChange={(event) => set('newCompanyGraceDays', event.target.value)}
          />

          <Checkbox
            label="Moderation required"
            hint="Off publishes every submission immediately, which is only sensible on a closed deployment."
            checked={Boolean(form.moderationRequired)}
            onChange={(event) => set('moderationRequired', event.target.checked)}
          />

          <Checkbox
            label="Feature previews enabled"
            checked={Boolean(form.previewEnabled)}
            onChange={(event) => set('previewEnabled', event.target.checked)}
          />

          <TextInput
            label="Preview tenant lifetime (minutes)"
            hint="How long a visitor's disposable demo company lives before it is deleted. 1440 = 24 hours."
            inputMode="numeric"
            value={String(form.previewSessionMinutes ?? '')}
            onChange={(event) => set('previewSessionMinutes', event.target.value)}
          />

          <Checkbox
            label="Assistant duplicate and spam suggestions"
            hint="Advisory only, shown on pending requests. Needs a model key on this deployment."
            checked={Boolean(form.aiDuplicateDetectionEnabled)}
            onChange={(event) => set('aiDuplicateDetectionEnabled', event.target.checked)}
          />

          <div>
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        </form>
      </WorkPage>
    </div>
  );
}
